import type { ReactNode } from "react";
import type { AttachmentInfo, CalEvent } from "../types";
import { isFocus, isUnsentDraft, needsReply } from "../domain/day";
import styles from "./CalendarModule.module.css";

/** Attachments named on a row before the rest are counted ("+2 more"). */
const NAMED_ATTACHMENTS = 2;
const ATTACHMENT_KIND: Readonly<Record<AttachmentInfo["kind"], string>> = { file: "Doc", reference: "Doc", item: "Item" };

/** "Doc · Client Brief.docx" tags, as in the mockup; the generic tag until the names are known. */
function attachmentTags(attachments: readonly AttachmentInfo[] | undefined): ReactNode[] {
  if (!attachments?.length) return [<span key="att" className={styles.tag}>Attachments</span>];
  const named = attachments.slice(0, NAMED_ATTACHMENTS).map((a) => (
    <span key={a.id || a.name} className={`${styles.tag} ${styles.tagName}`} title={a.name}>
      {`${ATTACHMENT_KIND[a.kind]} · ${a.name}`}
    </span>
  ));
  const more = attachments.length - NAMED_ATTACHMENTS;
  return more > 0 ? [...named, <span key="att-more" className={styles.tag}>+{more} more</span>] : named;
}

/** What a schedule row knows beyond the event, once read: its repeat, its attachments and its email thread's sender. */
export interface RowDetails {
  readonly repeat?: string;
  readonly attachments?: readonly AttachmentInfo[];
  readonly thread?: string;
}

/**
 * An event's status badges in the mockup's order (Draft, Conflict, Tentative, Needs Reply, Declined,
 * Cancelled, Focus), then its repeat and attachment tags.
 */
export function badgesFor(e: CalEvent, clashing: ReadonlySet<string>, details: RowDetails = {}): ReactNode[] {
  const out: ReactNode[] = [];
  if (isUnsentDraft(e)) out.push(<span key="draft" className={`${styles.badge} ${styles.badgeInfo}`}>Draft · Send in Outlook</span>);
  if (clashing.has(e.id)) out.push(<span key="conflict" className={`${styles.badge} ${styles.badgeErr}`}>Conflict</span>);
  if (e.response === "may") out.push(<span key="tent" className={`${styles.badge} ${styles.badgeWarn}`}>Tentative</span>);
  if (needsReply(e)) out.push(<span key="reply" className={`${styles.badge} ${styles.badgeNotice}`}>Needs Reply</span>);
  if (e.response === "no") out.push(<span key="dec" className={`${styles.badge} ${styles.badgeNeutral}`}>Declined</span>);
  if (e.isCancelled) out.push(<span key="cancel" className={`${styles.badge} ${styles.badgeNeutral}`}>Cancelled</span>);
  if (isFocus(e)) out.push(<span key="focus" className={`${styles.badge} ${styles.badgeNeutral}`}>Focus</span>);
  if (e.isRecurring) out.push(<span key="rec" className={styles.tag}>{details.repeat ?? "Recurring"}</span>);
  if (e.hasAttachments) out.push(...attachmentTags(details.attachments));
  if (details.thread) {
    const label = `Mail · ${details.thread} thread`;
    out.push(<span key="mail" className={`${styles.tag} ${styles.tagName}`} title={label}>{label}</span>);
  }
  return out;
}
